/* eslint-disable */
"use client";

import { Link, usePathname } from "./router";
import {
  LayoutDashboard,
  UsersRound,
  HandCoins,
  Wallet,
  Bus,
  SlidersHorizontal,
} from "lucide-react";
import { cn } from "./utils";

function LogoTile({ size = 40 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
    >
      <Wallet
        size={size}
        strokeWidth={1.8}
        style={{ color: "var(--accent)" }}
      />
    </span>
  );
}

const NAV = [
  { href: "/", label: "Dashboard", icon: LayoutDashboard },
  { href: "/employees", label: "Employees", icon: UsersRound },
  { href: "/loans", label: "Loans", icon: HandCoins },
  { href: "/payroll", label: "Payroll", icon: Wallet },
  { href: "/mobility-fund", label: "Mobility Fund", icon: Bus },
  { href: "/preferences", label: "Preferences", icon: SlidersHorizontal },
];

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname.startsWith(href);
}

export function TopNavBar() {
  const pathname = usePathname();
  return (
    <>
      <header className="sticky top-0 z-30 hidden w-full bg-[color-mix(in_oklab,var(--surface)_85%,transparent)] backdrop-blur-xl md:flex">
        <div className="mx-auto flex w-full max-w-[1400px] items-center gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <Link href="/" className="flex shrink-0 items-center gap-3">
            <LogoTile size={38} />
            <div className="hidden sm:block">
              <p className="font-display text-lg font-bold tracking-tight text-text">Payora</p>
              <p className="text-[9px] font-semibold tracking-[0.18em] text-faint uppercase">Payroll Suite</p>
            </div>
          </Link>

          <div className="mx-auto flex flex-1 items-center justify-center">
            <nav className="flex items-center gap-1 p-1 rounded-xl overflow-x-auto bg-[color-mix(in_oklab,var(--surface2)_80%,transparent)]">
              {NAV.map((item) => {
                const active = isActive(pathname, item.href);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "group relative flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-all duration-200 whitespace-nowrap sm:text-sm sm:px-4 sm:py-2.5",
                      active
                        ? "bg-[color-mix(in_oklab,var(--primary)_13%,transparent)] text-text"
                        : "text-muted hover:bg-surface3 hover:text-text"
                    )}
                  >
                    <span
                      className={cn(
                        "absolute bottom-0 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-t-full bg-accent transition-all duration-200",
                        active ? "opacity-100 scale-x-100" : "opacity-0 scale-x-50"
                      )}
                    />
                    <Icon className={cn("h-4 w-4 sm:h-[18px] sm:w-[18px] transition-colors", active ? "text-accent" : "text-faint group-hover:text-muted")} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="w-[38px] shrink-0 md:h-9" />
        </div>
      </header>

      <header className="sticky top-0 z-30 flex items-center justify-start gap-2.5 bg-[color-mix(in_oklab,var(--bg)_80%,transparent)] px-4 py-3 backdrop-blur-xl md:hidden">
        <Link href="/" className="flex items-center gap-2.5">
          <LogoTile size={34} />
          <span className="font-display text-lg font-bold text-text">Payora</span>
        </Link>
      </header>
    </>
  );
}

export function MobileNav() {
  const pathname = usePathname();
  return (
    <nav className="sticky bottom-0 z-30 grid grid-cols-6 bg-[color-mix(in_oklab,var(--surface)_90%,transparent)] backdrop-blur-xl md:hidden">
      {NAV.map((item) => {
        const active = isActive(pathname, item.href);
        const Icon = item.icon;
        return (
          <Link
            key={item.href}
            href={item.href}
            className={cn(
              "flex flex-col items-center gap-1 py-2.5 text-[9px] font-bold tracking-wide uppercase transition-colors",
              active ? "text-accent" : "text-faint"
            )}
          >
            <Icon className="h-5 w-5" />
            {item.label.split(" ")[0]}
          </Link>
        );
      })}
    </nav>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="rise-in mb-7 flex flex-wrap items-end justify-between gap-4">
      <div className="max-w-xl">
        {eyebrow && (
          <p className="mb-1.5 text-[11px] font-bold tracking-[0.2em] text-primary-strong uppercase">{eyebrow}</p>
        )}
        <h1 className="font-display text-2xl font-bold tracking-tight text-text sm:text-3xl">{title}</h1>
        {description && <p className="mt-1.5 text-sm leading-relaxed text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </div>
  );
}
